import React, { useMemo } from 'react';
import Footer from './components/Footer';
import Navbar from './components/Navbar';

interface MainLayoutProps {
  children: React.ReactNode;
  withFooter?: boolean;
  className?: string;
}

/**
 * MainLayout component that includes both Navbar and Footer
 *
 * @param {React.ReactNode} children - The content to render between the Navbar and Footer
 * @param {boolean} withFooter - Whether to include the Footer (default: true)
 * @param {string} className - Additional CSS classes to apply to the main content area
 */
const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  withFooter = true,
  className = '',
}) => {
  // Memoize the Navbar component to prevent unnecessary re-renders
  const MemoizedNavbar = useMemo(() => <Navbar />, []);

  // Memoize the Footer component to prevent unnecessary re-renders
  // Only create the Footer component if it's needed
  const MemoizedFooter = useMemo(
    () => (withFooter ? <Footer /> : null),
    [withFooter],
  );

  return (
    <div className="flex flex-col min-h-screen">
      {MemoizedNavbar}
      <Navbar />
      <main className={`flex-grow ${className}`}>{children}</main>
      {MemoizedFooter}
    </div>
  );
};

export default React.memo(MainLayout);
